import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, PieChart, Activity, Calendar } from 'lucide-react';
import { usageAPI } from '@/services/api';
import { formatNumber } from '@/utils/helpers';
import { Sidebar } from '@/components/Sidebar';
import type { UsageStatistics, UsageRecord } from '@/types';

export function StatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<UsageStatistics | null>(null);
  const [records, setRecords] = useState<UsageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, recordsRes] = await Promise.all([
        usageAPI.getStatistics(),
        usageAPI.getRecords({ limit: 20 }),
      ]);

      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
      if (recordsRes.success && recordsRes.data) {
        setRecords(recordsRes.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const operationNames: Record<string, string> = {
    video_info: '视频解析',
    hot_comments: '热门评论',
    search_comments: '搜索评论',
    reply_comment: '回复评论',
    batch_reply: '批量回复',
    hot_videos: '热门视频',
    search_videos: '搜索视频',
    sentiment_analysis: '倾向分析',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto min-w-0">
        {/* 头部 */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 h-14 flex items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>返回</span>
            </button>
            <h1 className="ml-4 text-lg font-semibold">使用统计</h1>
          </div>
        </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {stats && (
          <>
            {/* 概览卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={Activity}
                title="总操作次数"
                value={formatNumber(stats.total_operations)}
                color="blue"
              />
              <StatCard
                icon={PieChart}
                title="API 配额"
                value={`${stats.quota_total}`}
                subtitle={`剩余 ${stats.quota_remaining}`}
                color="green"
              />
              <StatCard
                icon={BarChart3}
                title="已使用"
                value={`${stats.quota_total - stats.quota_remaining}`}
                subtitle={`占比 ${((stats.quota_total - stats.quota_remaining) / stats.quota_total * 100).toFixed(1)}%`}
                color="orange"
              />
              <StatCard
                icon={Calendar}
                title="今日使用"
                value={`${stats.daily_usage[0]?.count || 0}`}
                color="purple"
              />
            </div>

            {/* 操作类型分布 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">操作类型分布</h3>
                <div className="space-y-3">
                  {Object.entries(stats.operations_by_type)
                    .filter(([, count]) => count > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => (
                      <div key={type} className="flex items-center">
                        <span className="w-24 text-sm text-gray-600">
                          {operationNames[type] || type}
                        </span>
                        <div className="flex-1 mx-3">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{
                                width: `${(count / stats.total_operations) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    ))}
                  {Object.values(stats.operations_by_type).every(c => c === 0) && (
                    <div className="text-center text-gray-400 py-4">
                      暂无数据
                    </div>
                  )}
                </div>
              </div>

              {/* 最近使用记录 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">最近使用</h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {records.length === 0 ? (
                    <div className="text-center text-gray-400 py-4">
                      暂无使用记录
                    </div>
                  ) : (
                    records.map((record) => (
                      <div
                        key={record.id}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div>
                          <div className="text-sm font-medium">
                            {operationNames[record.operation] || record.operation}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(record.created_at).toLocaleString()}
                          </div>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            record.success
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {record.success ? '成功' : '失败'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* 每日使用趋势 */}
            {stats.daily_usage.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">最近30天使用趋势</h3>
                <div className="flex items-end gap-2 h-32">
                  {stats.daily_usage.slice(0, 30).reverse().map((day) => {
                    const maxCount = Math.max(...stats.daily_usage.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div
                        key={day.date}
                        className="flex-1 flex flex-col items-center"
                      >
                        <div
                          className="w-full bg-primary-500 rounded-t hover:bg-primary-600 transition-colors"
                          style={{ height: `${height}%` }}
                          title={`${day.date}: ${day.count}次`}
                        />
                        <div className="text-[10px] text-gray-400 mt-1 rotate-45 origin-left translate-y-2">
                          {day.date.slice(5)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </main>
    </div>
  );
}

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  subtitle?: string;
  color: 'blue' | 'green' | 'orange' | 'purple';
}

function StatCard({ icon: Icon, title, value, subtitle, color }: StatCardProps) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-sm text-gray-500">{title}</div>
          <div className="text-2xl font-bold">{value}</div>
          {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}
